import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Building2, ShieldCheck, KeyRound, Users, ArrowRight, UserPlus, Network, Users2,
  Lock, BadgeCheck, ChevronRight, Layers, Info,
} from "lucide-react";
import {
  Button, Card, CardHeader, CardBody, Badge, SectionLabel, PageHeader,
  EmptyState, Loading, AccessDenied, Alert, Field, TextInput, Tabs, Textarea, Select, Modal,
} from "@/components/redvolt/kit";
import { roleLabel, accessSummary } from "@/lib/permissions";
import ConnectionsPage from "@/app/ConnectionsPage";
import SharingPage from "@/app/SharingPage";
import CoordinationPage from "@/app/CoordinationPage";
import PlatformAdminConsole from "@/app/PlatformAdminConsole";
import PeoplePage from "@/app/PeoplePage";

/* --------------------- Platform administrator claim -------------------- */
const ClaimBanner = ({ api, onReload }) => {
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/platform/summary").then((r) => setSummary(r.data)).catch(() => {}); }, [api]);
  if (!summary || !summary.can_claim) return null;
  const claim = async () => {
    setBusy(true);
    try {
      await api.post("/platform/bootstrap");
      toast.success("You are now the RedVolt platform administrator");
      onReload();
    } catch { toast.error("Could not claim platform administration"); }
    finally { setBusy(false); }
  };
  return (
    <Alert variant="info" title="Platform setup" className="items-center">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span>No platform administrator exists yet. As the platform owner, you can claim administration for RedVolt.</span>
        <Button size="sm" onClick={claim} disabled={busy} data-testid="claim-platform-admin">
          {busy ? "Claiming…" : "Claim platform administration"}
        </Button>
      </div>
    </Alert>
  );
};

/* ------------------------- No organization yet -------------------------- */
const Onboarding = ({ api, onReload }) => {
  return (
    <div className="mx-auto max-w-xl py-6" data-testid="no-membership-state">
      <PageHeader title="Welcome to RedVolt" subtitle="You're signed in, but you don't belong to an organization yet." />
      <Card className="mt-6">
        <CardBody>
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-lg bg-accent text-primary">
              <Building2 className="h-5 w-5" />
            </span>
            <div>
              <h3 className="text-base font-semibold text-slate-900">Get set up with an organization</h3>
              <p className="mt-1 text-sm text-slate-500">
                RedVolt organizations are set up by invitation or by a RedVolt administrator.
                If someone invited you, open your invitation link to join. Otherwise, ask your
                RedVolt administrator to set up your organization.
              </p>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

/* ------------------------------ Home view ------------------------------- */
const StatRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 py-2">
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-sm font-semibold text-slate-900 text-right truncate">{value}</span>
  </div>
);

const HomeView = ({ me, active, context, memberships, onNavigate, api, onReload }) => {
  const caps = context?.capabilities || [];
  const roleName = roleLabel(context?.role, context?.access_source);
  const [home, setHome] = useState(null);
  useEffect(() => {
    if (!active?.tenant_id) return;
    api.get(`/tenants/${active.tenant_id}/home`).then((r) => setHome(r.data)).catch(() => {});
  }, [api, active?.tenant_id]);

  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const name = home?.user?.name || (me.user.name || me.user.email?.split("@")[0]) || "there";
  const setup = home?.setup;
  const attention = home?.attention || [];

  const actions = [
    caps.includes("people:create") && { k: "add-person", label: "Add a person", icon: Users, to: "people" },
    caps.includes("member:invite") && { k: "invite", label: "Invite member", icon: UserPlus, to: "organization" },
    caps.includes("tenant:manage") && { k: "edit-org", label: "Edit organization", icon: Building2, to: "organization" },
    caps.includes("relationship:request") && { k: "connect", label: "Request connection", icon: Network, to: "organization" },
    caps.includes("coordination:manage_group") && { k: "coordinate", label: "Start a coordination effort", icon: Users2, to: "coordination" },
  ].filter(Boolean);

  return (
    <div className="space-y-6" data-testid="home-view">
      {api && onReload && <ClaimBanner api={api} onReload={onReload} />}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{greet}, {name}</h1>
        <p className="mt-0.5 text-sm text-slate-500">Here's your organization at a glance.</p>
      </div>

      {/* Active organization */}
      <Card highlighted data-testid="active-context-card">
        <CardBody className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-lg bg-accent text-primary">
              <Building2 className="h-5 w-5" />
            </span>
            <div>
              <SectionLabel>Active Organization</SectionLabel>
              <p className="text-lg font-bold text-slate-900" data-testid="active-tenant-name">{active.tenant_name}</p>
            </div>
          </div>
          <Badge variant="active" data-testid="active-role"><BadgeCheck className="h-3.5 w-3.5" /> {roleName}</Badge>
        </CardBody>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Setup progress */}
        <Card data-testid="setup-progress">
          <CardHeader title="Get your organization ready"
            action={setup && <span className="text-xs font-semibold text-slate-500">Setup: {setup.completed} of {setup.total} complete</span>} />
          <CardBody className="pt-3">
            {!setup ? <Loading label="Loading…" /> : (
              <ul className="space-y-2">
                {setup.items.map((it) => (
                  <li key={it.key} className="flex items-center gap-2.5 text-sm" data-testid={`setup-${it.key}`}>
                    <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full ${it.done ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
                      {it.done ? <BadgeCheck className="h-3.5 w-3.5" /> : <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />}
                    </span>
                    <span className={it.done ? "text-slate-500 line-through" : "text-slate-700"}>{it.label}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        {/* Needs attention */}
        <Card data-testid="needs-attention">
          <CardHeader title="Needs your attention" icon={Info} />
          <CardBody className="pt-3">
            {attention.length === 0 ? (
              <p className="text-sm text-slate-500">Nothing needs your attention right now.</p>
            ) : (
              <ul className="space-y-2">
                {attention.map((a) => (
                  <li key={a.key}>
                    <button onClick={() => onNavigate(a.nav)} data-testid={`attention-${a.key}`}
                      className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50">
                      <span>{a.label}</span><ChevronRight className="h-4 w-4 text-slate-400" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Quick actions */}
      {actions.length > 0 && (
        <Card>
          <CardHeader title="Quick actions" icon={ArrowRight} />
          <CardBody className="pt-4">
            <div className="flex flex-wrap gap-2">
              {actions.map((a) => (
                <Button key={a.k} variant="outline" size="sm" onClick={() => onNavigate(a.to)} data-testid={`qa-${a.k}`}>
                  <a.icon className="h-4 w-4" /> {a.label}
                </Button>
              ))}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

/* -------------------------- Organization view --------------------------- */
const ROLE_OPTIONS = [
  ["owner", "Owner"], ["admin", "Administrator"], ["campaign_manager", "Campaign Manager"],
  ["staff", "Staff"], ["volunteer", "Volunteer"], ["viewer", "Viewer"],
];

const InviteForm = ({ api, tenantId, onDone }) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("staff");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");
  const [emailSent, setEmailSent] = useState(null);
  const submit = async () => {
    if (!email.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/invitations`, { email: email.trim(), role });
      const url = r.data.invite_url || `${window.location.origin}/?invite=${r.data.token}`;
      setLink(url);
      setEmailSent(r.data.email_sent);
      if (r.data.email_sent) {
        toast.success(`Invitation emailed to ${r.data.email}`);
      } else {
        toast.warning(`Invitation created — email not delivered. Copy the link below and send it to ${r.data.email}.`);
      }
      setEmail("");
      onDone();
    } catch (e) {
      toast.error(e?.response?.status === 400 ? "Enter a valid email and role" : "Could not create invitation");
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Invite a team member</p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="person@example.com" className="flex-1" data-testid="invite-email" />
        <Select value={role} onChange={(e) => setRole(e.target.value)} data-testid="invite-role" className="sm:w-52">
          {ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </Select>
        <Button size="sm" onClick={submit} disabled={busy || !email.trim()} data-testid="invite-submit">
          <UserPlus className="mr-1.5 h-4 w-4" /> {busy ? "Sending…" : "Send invite"}
        </Button>
      </div>
      {link && (
        <div className={`mt-2 rounded p-2 text-xs ${emailSent ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`} data-testid="invite-link">
          {emailSent
            ? "Invitation email sent. You can also share this secure link (expires in 14 days):"
            : "Email was not delivered — share this secure invitation link yourself (expires in 14 days):"}
          <br />
          <span className="break-all font-mono text-[11px] text-slate-700">{link}</span>
        </div>
      )}
    </div>
  );
};

const MembersModule = ({ api, tenantId, capabilities }) => {
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [linkFor, setLinkFor] = useState(null);
  const canView = capabilities.includes("member:view");
  const canInvite = capabilities.includes("member:invite");
  const canManage = capabilities.includes("member:manage");

  const load = async () => {
    if (!canView) { setDenied(true); setLoading(false); return; }
    try {
      const r = await api.get(`/tenants/${tenantId}/members`);
      setMembers(r.data.members || []); setDenied(false);
      const iv = await api.get(`/tenants/${tenantId}/invitations`).then((x) => x.data.invitations).catch(() => []);
      setInvites(iv);
    } catch (e) { if (e.response?.status === 403) setDenied(true); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [api, tenantId, canView]);

  const changeRole = async (m, role) => {
    try { await api.patch(`/tenants/${tenantId}/members/${m.membership_id}/role`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail === "cannot_remove_last_owner" ? "Can't remove the last owner" : "Could not change role"); }
  };
  const deactivate = async (m) => {
    try { await api.post(`/tenants/${tenantId}/members/${m.membership_id}/deactivate`); toast.success("Member deactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail === "cannot_remove_last_owner" ? "Can't remove the last owner" : "Could not deactivate"); }
  };
  const revokeInvite = async (id) => {
    try { await api.post(`/tenants/${tenantId}/invitations/${id}/revoke`); load(); } catch { toast.error("Could not revoke"); }
  };
  const unlinkPerson = async (m) => {
    try { await api.delete(`/tenants/${tenantId}/members/${m.membership_id}/person-link`); toast.success("Unlinked"); load(); }
    catch { toast.error("Could not unlink"); }
  };
  const doLink = async (personId) => {
    try { await api.post(`/tenants/${tenantId}/members/${linkFor.membership_id}/person-link`, { person_id: personId }); toast.success("Linked to person"); setLinkFor(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail === "already_linked" ? "That person or member is already linked" : "Could not link"); }
  };

  if (loading) return <Loading label="Loading members…" />;
  if (denied) return <AccessDenied message="Your role doesn't include permission to view members." />;
  return (
    <div className="space-y-4">
      {canInvite && <InviteForm api={api} tenantId={tenantId} onDone={load} />}
      {members.length === 0 ? (
        <EmptyState icon={Users} title="Build your team"
          description="Invite committee members, staff, volunteers, or other trusted users and give them the appropriate role." />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-2.5">Person</th>
                <th className="px-4 py-2.5">Organization Role</th>
                <th className="px-4 py-2.5">RedVolt Access</th>
                <th className="px-4 py-2.5">Status</th>{canManage && <th className="px-4 py-2.5"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {members.map((m) => {
                const primary = m.name || (m.email ? m.email.split("@")[0] : "Member");
                return (
                  <tr key={m.membership_id} className="hover:bg-slate-50" data-testid={`member-${m.membership_id}`}>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-3">
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-secondary text-xs font-bold text-white">{(primary[0] || "U").toUpperCase()}</span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-800">{primary}</p>
                          {m.email && <p className="truncate text-xs text-slate-500">{m.email}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      {m.organizational_roles && m.organizational_roles.length > 0 ? (
                        <div className="flex flex-wrap gap-1" data-testid={`org-roles-${m.membership_id}`}>
                          {m.organizational_roles.map((r) => <Badge key={r} variant="neutral">{r}</Badge>)}
                        </div>
                      ) : m.person_id ? (
                        <span className="text-xs text-slate-400">No position assigned</span>
                      ) : canManage ? (
                        <Button variant="outline" size="sm" onClick={() => setLinkFor(m)} data-testid={`link-person-${m.membership_id}`}>Link to a person</Button>
                      ) : <span className="text-xs text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      {canManage ? (
                        <Select value={m.role || "viewer"} onChange={(e) => changeRole(m, e.target.value)} data-testid={`role-select-${m.membership_id}`} className="w-44">
                          {ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </Select>
                      ) : <span className="text-sm text-slate-700">{roleLabel(m.role)}</span>}
                    </td>
                    <td className="px-4 py-2.5"><Badge variant={m.status === "active" ? "implemented" : "neutral"}>{m.status}</Badge></td>
                    {canManage && <td className="px-4 py-2.5 text-right">
                      {m.person_id && <Button variant="ghost" size="sm" onClick={() => unlinkPerson(m)} data-testid={`unlink-person-${m.membership_id}`}>Unlink</Button>}
                      {m.status === "active" && <Button variant="ghost" size="sm" onClick={() => deactivate(m)} data-testid={`deactivate-${m.membership_id}`}>Remove</Button>}
                    </td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {invites.length > 0 && (
        <div data-testid="pending-invites">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Pending invitations</p>
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {invites.map((i) => (
              <li key={i.id} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`invite-${i.id}`}>
                <span className="text-slate-700">{i.email} · <span className="text-slate-500">{roleLabel(i.role)}</span></span>
                {canManage && <Button variant="ghost" size="sm" onClick={() => revokeInvite(i.id)}>Revoke</Button>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {linkFor && <LinkPersonModal api={api} tenantId={tenantId} member={linkFor} onClose={() => setLinkFor(null)} onPick={doLink} />}
    </div>
  );
};

const LinkPersonModal = ({ api, tenantId, member, onClose, onPick }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try { const r = await api.get(`/tenants/${tenantId}/people`, { params: q ? { q, limit: 10 } : { limit: 10 } }); setResults(r.data.items || []); }
      catch { setResults([]); } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, api, tenantId]);
  return (
    <Modal open onClose={onClose} title={`Link ${member.name || member.email} to a person`}
      description="Choose the People record that represents this member. This links identities only — it does not change RedVolt access."
      testid="link-person-modal"
      footer={<Button variant="ghost" onClick={onClose} data-testid="link-cancel">Cancel</Button>}>
      <TextInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people by name, email, phone…" data-testid="link-search" />
      <div className="mt-3 max-h-64 space-y-1 overflow-y-auto">
        {loading && <p className="text-sm text-slate-400">Searching…</p>}
        {!loading && results.length === 0 && <p className="text-sm text-slate-400">No people found. Add them under People first.</p>}
        {results.map((p) => (
          <button key={p.id} onClick={() => onPick(p.id)} data-testid={`link-pick-${p.id}`}
            className="flex w-full items-center justify-between rounded-md border border-slate-100 px-3 py-2 text-left text-sm hover:bg-slate-50">
            <span className="font-medium text-slate-800">{p.display_name}</span>
            <span className="text-xs text-primary">Link</span>
          </button>
        ))}
      </div>
    </Modal>
  );
};

const ROLE_DESCRIPTIONS = [
  ["Owner", "Full organization leadership, including the most sensitive organization approvals."],
  ["Administrator", "Manage most settings, team members, connections, coordination, and sharing — but not Owner-only sensitive approvals."],
  ["Campaign Manager", "Perform approved operational and coordination functions, invite members, and manage resources."],
  ["Staff", "Access appropriate organization information and shared resources."],
  ["Volunteer", "Limited operational access."],
  ["Viewer", "Read-only access where authorized."],
];

const RolesTab = ({ api, tenantId, caps }) => {
  const canManage = caps.includes("tenant:manage");
  const mark = async () => { try { await api.post(`/tenants/${tenantId}/roles-reviewed`); toast.success("Roles reviewed"); } catch { toast.error("Could not save"); } };
  return (
    <div className="space-y-3" data-testid="roles-tab">
      <p className="text-sm text-slate-500">Roles determine what each team member can do. Assign roles from the Members tab.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {ROLE_DESCRIPTIONS.map(([name, desc]) => (
          <Card key={name}><CardBody className="py-3">
            <p className="text-sm font-semibold text-slate-900">{name}</p>
            <p className="mt-1 text-sm text-slate-500">{desc}</p>
          </CardBody></Card>
        ))}
      </div>
      {canManage && <Button variant="outline" size="sm" onClick={mark} data-testid="roles-reviewed">I've reviewed roles</Button>}
    </div>
  );
};

const ProfileTab = ({ api, tenantId, caps }) => {
  const canManage = caps.includes("tenant:manage");
  const [p, setP] = useState(null);
  const [types, setTypes] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/tenants/${tenantId}/profile`).then((r) => setP(r.data.profile || {})).catch(() => setP({}));
    api.get("/org-types").then((r) => setTypes(r.data.org_types)).catch(() => {});
  }, [api, tenantId]);
  if (!p) return <Loading label="Loading profile…" />;
  const set = (k, v) => setP({ ...p, [k]: v });
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/tenants/${tenantId}/profile`, {
        display_name: p.display_name, legal_name: p.legal_name, org_type: p.org_type,
        state: p.state, jurisdiction: p.jurisdiction, short_description: p.short_description,
        city: p.city, county: p.county, address_line1: p.address_line1,
        address_line2: p.address_line2, postal_code: p.postal_code });
      toast.success("Organization profile saved");
    } catch { toast.error("Could not save profile"); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-2xl space-y-4" data-testid="profile-tab">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Display name" htmlFor="p-display"><TextInput id="p-display" value={p.display_name || ""} disabled={!canManage} onChange={(e) => set("display_name", e.target.value)} data-testid="profile-display-name" /></Field>
        <Field label="Legal name" htmlFor="p-legal"><TextInput id="p-legal" value={p.legal_name || ""} disabled={!canManage} onChange={(e) => set("legal_name", e.target.value)} /></Field>
        <Field label="Organization type" htmlFor="p-type">
          <Select id="p-type" value={p.org_type || ""} disabled={!canManage} onChange={(e) => set("org_type", e.target.value)} data-testid="profile-org-type">
            <option value="">Select…</option>
            {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </Select>
        </Field>
        <Field label="Jurisdiction" htmlFor="p-jur"><TextInput id="p-jur" value={p.jurisdiction || ""} disabled={!canManage} onChange={(e) => set("jurisdiction", e.target.value)} /></Field>
      </div>
      <div className="border-t border-slate-100 pt-4">
        <SectionLabel>Location</SectionLabel>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <Field label="Address line 1" htmlFor="p-addr1"><TextInput id="p-addr1" value={p.address_line1 || ""} disabled={!canManage} onChange={(e) => set("address_line1", e.target.value)} placeholder="Street address" data-testid="profile-address-line1" /></Field>
          <Field label="Address line 2" htmlFor="p-addr2"><TextInput id="p-addr2" value={p.address_line2 || ""} disabled={!canManage} onChange={(e) => set("address_line2", e.target.value)} placeholder="Suite / unit (optional)" data-testid="profile-address-line2" /></Field>
          <Field label="City / Town" htmlFor="p-city"><TextInput id="p-city" value={p.city || ""} disabled={!canManage} onChange={(e) => set("city", e.target.value)} data-testid="profile-city" /></Field>
          <Field label="County / Parish" htmlFor="p-county"><TextInput id="p-county" value={p.county || ""} disabled={!canManage} onChange={(e) => set("county", e.target.value)} data-testid="profile-county" /></Field>
          <Field label="State" htmlFor="p-state"><TextInput id="p-state" value={p.state || ""} disabled={!canManage} onChange={(e) => set("state", e.target.value)} data-testid="profile-state" /></Field>
          <Field label="Postal / ZIP code" htmlFor="p-zip"><TextInput id="p-zip" value={p.postal_code || ""} disabled={!canManage} onChange={(e) => set("postal_code", e.target.value)} data-testid="profile-postal-code" /></Field>
        </div>
      </div>
      <Field label="Short description" htmlFor="p-desc"><Textarea id="p-desc" value={p.short_description || ""} disabled={!canManage} onChange={(e) => set("short_description", e.target.value)} rows={3} /></Field>
      {canManage && <Button onClick={save} disabled={busy} data-testid="profile-save">{busy ? "Saving…" : "Save profile"}</Button>}
    </div>
  );
};

const OrganizationView = ({ api, active, context, onNavigate }) => {
  const caps = context?.capabilities || [];
  const [tab, setTab] = useState("overview");
  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "members", label: "Members" },
    { key: "roles", label: "Roles" },
    { key: "connections", label: "Connections" },
    { key: "sharing", label: "Sharing" },
  ];
  return (
    <div className="space-y-5">
      <PageHeader title="Organization" subtitle={active.tenant_name} />
      <Tabs tabs={tabs} value={tab} onChange={setTab} />
      <div>
        {tab === "overview" && <ProfileTab api={api} tenantId={active.tenant_id} caps={caps} />}
        {tab === "members" && <MembersModule api={api} tenantId={active.tenant_id} capabilities={caps} />}
        {tab === "roles" && <RolesTab api={api} tenantId={active.tenant_id} caps={caps} />}
        {tab === "connections" && <ConnectionsPage api={api} active={active} caps={caps} onNavigate={onNavigate} />}
        {tab === "sharing" && <SharingPage api={api} active={active} caps={caps} />}
      </div>
    </div>
  );
};

/* ------------------------------ Account view ---------------------------- */
const AccountView = ({ me, context, onSignOut }) => {
  const roleName = roleLabel(context?.role, context?.access_source);
  return (
    <div className="space-y-6">
      <PageHeader title="Account" subtitle="Your profile and security." />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="Profile" icon={BadgeCheck} />
          <CardBody className="pt-2">
            {me.user.name && <><StatRow label="Name" value={me.user.name} /><div className="h-px bg-slate-100" /></>}
            <StatRow label="Email" value={me.user.email} />
            <div className="h-px bg-slate-100" />
            <StatRow label="Role" value={roleName} />
            <div className="h-px bg-slate-100" />
            <StatRow label="Account status" value={<Badge variant="implemented">{me.user.status}</Badge>} />
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Security" icon={ShieldCheck} />
          <CardBody className="pt-2 space-y-2.5">
            {[
              { icon: Lock, t: "Signed in securely with multi-factor protection" },
              { icon: Layers, t: "Your organization's data is kept separate and private" },
              { icon: KeyRound, t: "Access is limited to what your role allows" },
            ].map((s) => (
              <div key={s.t} className="flex items-start gap-2.5">
                <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-md bg-emerald-50 text-emerald-600">
                  <s.icon className="h-3.5 w-3.5" />
                </span>
                <span className="text-sm text-slate-700">{s.t}</span>
              </div>
            ))}
            <div className="pt-2">
              <Button variant="outline" size="sm" onClick={onSignOut} data-testid="account-sign-out">Sign out</Button>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

/* ------------------------------- Router --------------------------------- */
export default function Dashboard({ api, me, memberships, active, view, onNavigate, onReload, onSignOut }) {
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  const tenantId = active?.tenant_id;

  useEffect(() => {
    let cancel = false;
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);
    setDenied(false);
    (async () => {
      try {
        const r = await api.get(`/tenants/${tenantId}/context`);
        if (!cancel) setContext(r.data);
      } catch (e) {
        if (!cancel) {
          setContext(null);
          if (e.response?.status === 403) setDenied(true);
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [api, tenantId]);

  if (view === "admin") return <PlatformAdminConsole api={api} me={me} />;
  if (!active) return (
    <div className="space-y-6" data-testid="no-org-home">
      <ClaimBanner api={api} onReload={onReload} />
      <Onboarding api={api} onReload={onReload} />
    </div>
  );
  if (loading) return <Loading label="Loading organization…" />;
  if (denied) return <AccessDenied message="You don't have access to this organization's context." />;

  const caps = context?.capabilities || [];
  if (view === "people") return <PeoplePage api={api} active={active} caps={caps} />;
  if (view === "coordination") return <CoordinationPage api={api} active={active} caps={caps} />;
  if (view === "organization" || view === "connections" || view === "sharing")
    return <OrganizationView api={api} active={active} context={context} onNavigate={onNavigate} />;
  if (view === "account") return <AccountView me={me} context={context} onSignOut={onSignOut} />;
  return <HomeView me={me} active={active} context={context} memberships={memberships} onNavigate={onNavigate} api={api} onReload={onReload} />;
}
